import type { Hero, Layer } from "./types";

/** Reproducible PRNG used when callers want deterministic output. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Weighted random pick. Falls back to uniform when all weights are zero. */
export function pickWeighted<T extends { weight: number }>(items: T[], rand: () => number): T {
  const total = items.reduce((s, t) => s + Math.max(0, t.weight), 0);
  if (total <= 0) {
    return items[Math.floor(rand() * items.length)] ?? items[0]!;
  }
  let r = rand() * total;
  for (const t of items) {
    r -= Math.max(0, t.weight);
    if (r <= 0) return t;
  }
  return items[items.length - 1]!;
}

/** Stable serialization of a layerId→traitId selection, for dedup keys. */
export function comboKey(selection: Record<string, string>): string {
  return Object.keys(selection)
    .sort()
    .map((k) => `${k}:${selection[k]}`)
    .join("|");
}

/** Translate a hero's `pinned` into the initial selection map (validates trait ids exist). */
export function applyHero(layers: Layer[], hero: Hero | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!hero) return out;
  for (const l of layers) {
    const pinned = hero.pinned[l.id];
    if (pinned && l.traits.some((t) => t.id === pinned)) {
      out[l.id] = pinned;
    }
  }
  return out;
}

export const MAX_TOKEN_ATTEMPTS = 200;
